import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { KeycloakService } from '../../security/keycloak.service';
import { UserAreaPreferenceService } from '../../services/user-area-preference.service';
import { UserAreaPreference } from '../../models/user-area-preference.model';

type AreaPreset = {
  label: string;
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
};

@Component({
  selector: 'app-home',
  imports: [CommonModule, FormsModule],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class HomeComponent {
  private keycloakService = inject(KeycloakService);
  private areaService = inject(UserAreaPreferenceService);

  canEditArea = this.keycloakService.canEditAreaPreference();

  loading = signal(false);
  saving = signal(false);
  deleting = signal(false);
  hasSavedArea = signal(false);
  message = signal<string | null>(null);
  error = signal<string | null>(null);
  validationErrors = signal<string[]>([]);

  areaPresets: AreaPreset[] = [
    {
      label: 'Black Sea',
      minLatitude: 40.8,
      maxLatitude: 47.3,
      minLongitude: 27.0,
      maxLongitude: 42.2,
    },
    {
      label: 'Mediterranean Sea',
      minLatitude: 30.0,
      maxLatitude: 46.5,
      minLongitude: -6.5,
      maxLongitude: 36.5,
    },
    {
      label: 'Adriatic Sea',
      minLatitude: 39.5,
      maxLatitude: 45.9,
      minLongitude: 12.0,
      maxLongitude: 20.1,
    },
    {
      label: 'Aegean Sea',
      minLatitude: 35.0,
      maxLatitude: 41.5,
      minLongitude: 22.5,
      maxLongitude: 28.8,
    },
    {
      label: 'Baltic Sea',
      minLatitude: 53.5,
      maxLatitude: 66.0,
      minLongitude: 9.0,
      maxLongitude: 31.5,
    },
    {
      label: 'North Sea',
      minLatitude: 51.0,
      maxLatitude: 62.5,
      minLongitude: -4.5,
      maxLongitude: 9.5,
    },
    {
      label: 'Irish Sea',
      minLatitude: 52.0,
      maxLatitude: 55.6,
      minLongitude: -6.6,
      maxLongitude: -2.6,
    },
    {
      label: 'Atlantic Europe',
      minLatitude: 35.0,
      maxLatitude: 66.5,
      minLongitude: -25.0,
      maxLongitude: 5.0,
    },
    {
      label: 'Norwegian Sea',
      minLatitude: 62.0,
      maxLatitude: 72.5,
      minLongitude: -10.0,
      maxLongitude: 20.0,
    },
  ];

  applyPreset(preset: AreaPreset): void {
    this.area.minLatitude = preset.minLatitude;
    this.area.maxLatitude = preset.maxLatitude;
    this.area.minLongitude = preset.minLongitude;
    this.area.maxLongitude = preset.maxLongitude;

    this.validationErrors.set([]);
    this.message.set(null);
    this.error.set(null);
  }

  area: UserAreaPreference = this.getDefaultArea();

  ngOnInit(): void {
    if (this.canEditArea) {
      this.loadArea();
    }
  }

  loadArea(): void {
    this.loading.set(true);
    this.error.set(null);

    this.areaService.get().subscribe({
      next: (area) => {
        if (area) {
          this.area = area;
          this.hasSavedArea.set(true);
        } else {
          this.area = this.getDefaultArea();
          this.hasSavedArea.set(false);
        }

        this.loading.set(false);
      },
      error: () => {
        this.error.set('Failed to load area preference.');
        this.loading.set(false);
      },
    });
  }

  saveArea(): void {
    const errors = this.validateArea();

    this.validationErrors.set(errors);
    this.message.set(null);
    this.error.set(null);

    if (errors.length > 0) {
      return;
    }

    this.saving.set(true);

    this.areaService.save(this.area).subscribe({
      next: (saved) => {
        this.area = saved;
        this.hasSavedArea.set(true);
        this.message.set('Area preference saved.');
        this.saving.set(false);
      },
      error: (err) => {
        const backendMessage =
          err?.error?.message ||
          err?.error?.error ||
          'Failed to save area preference. Check the coordinates and try again.';

        this.error.set(backendMessage);
        this.saving.set(false);
      },
    });
  }

  private validateArea(): string[] {
    const errors: string[] = [];

    const minLat = Number(this.area.minLatitude);
    const maxLat = Number(this.area.maxLatitude);
    const minLon = Number(this.area.minLongitude);
    const maxLon = Number(this.area.maxLongitude);

    if (
      this.area.minLatitude === null ||
      this.area.minLatitude === undefined ||
      this.area.maxLatitude === null ||
      this.area.maxLatitude === undefined ||
      this.area.minLongitude === null ||
      this.area.minLongitude === undefined ||
      this.area.maxLongitude === null ||
      this.area.maxLongitude === undefined
    ) {
      errors.push('All coordinate fields are required. Fill in minimum and maximum latitude and longitude.');
      return errors;
    }

    if ([minLat, maxLat, minLon, maxLon].some(value => Number.isNaN(value))) {
      errors.push('Coordinates must be valid numbers. Remove letters or special characters.');
      return errors;
    }

    if (minLat < -90 || minLat > 90) {
      errors.push('Minimum latitude must be between -90 and 90.');
    }

    if (maxLat < -90 || maxLat > 90) {
      errors.push('Maximum latitude must be between -90 and 90.');
    }

    if (minLon < -180 || minLon > 180) {
      errors.push('Minimum longitude must be between -180 and 180.');
    }

    if (maxLon < -180 || maxLon > 180) {
      errors.push('Maximum longitude must be between -180 and 180.');
    }

    if (minLat > maxLat) {
      errors.push('Minimum latitude cannot be greater than maximum latitude. Swap the latitude values.');
    }

    if (minLon > maxLon) {
      errors.push('Minimum longitude cannot be greater than maximum longitude. Swap the longitude values.');
    }

    return errors;
  }

  deleteArea(): void {
    this.deleting.set(true);
    this.message.set(null);
    this.error.set(null);

    this.areaService.delete().subscribe({
      next: () => {
        this.area = this.getDefaultArea();
        this.hasSavedArea.set(false);
        this.message.set('Area preference deleted. You will see all alerts again.');
        this.deleting.set(false);
      },
      error: () => {
        this.error.set('Failed to delete area preference.');
        this.deleting.set(false);
      },
    });
  }

  private getDefaultArea(): UserAreaPreference {
    return {
      minLatitude: 43,
      maxLatitude: 46,
      minLongitude: 27,
      maxLongitude: 31,
      includeUnknownLocationAlerts: true,
      enabled: true,
    };
  }
}
